import React, { useEffect, useState } from "react";
import { Program } from "../../../core/types/types";
import { useSelector } from "react-redux";
import apiInstance from "../../../utility/apiInstance";
import CustomSelect from "../../../core/common/commonSelect";
import {
  FieldOfStudy,
  ProgramLevels,
} from "../../../core/common/selectOption/json/selectOption";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import BackendImage from "../../../core/common/backendImage";

interface PopupProps {
  show: boolean;
  onClose: () => void;
  program: Program | null;
  programUpdateHandler: (data: any) => void;
  deleteProgramHandler: (data: any) => void;
  addProgramHandler: (data: any) => void;
}

const AddProgramPopup: React.FC<PopupProps> = ({
  show,
  onClose,
  program,
  programUpdateHandler,
  deleteProgramHandler,
  addProgramHandler,
}) => {
  const user = useSelector((state: any) => state.auth.user);

  const [formData, setFormData] = useState<Program | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);

  const [msg, setMsg] = useState({
    error: false,
    loading: false,
    text: "",
  });

  const [errorField, setErrorField] = useState('');

  const getModalContainer = () => {
    const modalElement = document.getElementById("add-program-pp");
    return modalElement ? modalElement : document.body;
  };

  // ✅ Initialize form when modal opens or program changes
  useEffect(() => {
    setFormData({
      id: program?.id || "",
      programName: program?.programName || "",
      about: program?.about || "",
      requirement: program?.requirement || "",
      institution: program?.institution || user?.institution?.id,
      applicationFee: program?.applicationFee || 0,
      tuitionFee: program?.tuitionFee || 0,
      programLevel: program?.programLevel || "undergraduate",
      field: program?.field || "arts",
      start: program?.start || "",
      end: program?.end || "",
      image: program?.image || null,
    });
  }, [program, show, user]);

  // ✅ Reset message when modal opens
  useEffect(() => {
    if (show) {
      setMsg({ error: false, loading: false, text: "" });
    }
  }, [show]);

  // ✅ Set preview if editing a program with an image, reset otherwise
  useEffect(() => {
    setPreview(program?.image?.url || null);
  }, [program, show]);

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prevState) =>
      prevState
        ? {
            ...prevState,
            [name]: value,
          }
        : null
    );
    if (errorField === name) setErrorField('');
  };

  const sanitizeNumber = (value: string) => {
    const numericOnly = value.replace(/[^0-9.]/g, '');
    const parts = numericOnly.split('.');
    return parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : numericOnly;
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.value = sanitizeNumber(e.target.value);
    handleInputChange(e);
  };

  // ✅ Upload image to Payload CMS
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formDataMedia = new FormData();
    formDataMedia.append("file", file);
    formDataMedia.append("alt", "Program image");

    try {
      setUploading(true);
      const res = await apiInstance.post("/api/media", formDataMedia, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const uploadedMedia = res.data?.doc || res.data;
      const imageUrl = uploadedMedia.url || uploadedMedia.filename || "";

      // Save media ID in Program
      setFormData((prev) => ({
        ...prev!,
        image: uploadedMedia.id, // ✅ if image is an ID reference
      }));

      setPreview(imageUrl);
    } catch (error) {
      console.error("Image upload failed:", error);
      alert("Image upload failed!");
    } finally {
      setUploading(false);
    }
  };

  const saveProgram = async () => {
    setMsg({
      error: false,
      loading: true,
      text: "",
    });

    if (!formData || !formData.programName?.trim()) {
      setErrorField('programName');
      setMsg({
        error: true,
        loading: false,
        text: "Program name is required",
      });
      return;
    } else if (!formData.programLevel?.trim()) {
      setErrorField('programLevel');
      setMsg({
        error: true,
        loading: false,
        text: "Program level is required",
      });
      return;
    } else if (!formData.field?.trim()) {
      setErrorField('field');
      setMsg({
        error: true,
        loading: false,
        text: "Field of study is required",
      });
      return;
    } else if (String(formData.applicationFee ?? '') !== '' && Number(formData.applicationFee) < 0) {
      setErrorField('applicationFee');
      setMsg({
        error: true,
        loading: false,
        text: "Application fee cannot be negative",
      });
      return;
    } else if (String(formData.tuitionFee ?? '') !== '' && Number(formData.tuitionFee) < 0) {
      setErrorField('tuitionFee');
      setMsg({
        error: true,
        loading: false,
        text: "Tuition fee cannot be negative",
      });
      return;
    } else if (formData.start && formData.end && new Date(formData.end) < new Date(formData.start)) {
      setErrorField('end');
      setMsg({
        error: true,
        loading: false,
        text: "End date must be on or after the start date",
      });
      return;
    }

    setErrorField('');

    try {
      let res;
      if (program) {
        res = await apiInstance.patch(`/api/programs/${program.id}`, formData);
        programUpdateHandler(res.data.doc);
        setMsg({
          error: false,
          loading: false,
          text: "Program updated successfully!",
        });
      } else {
        res = await apiInstance.post(`/api/programs`, formData);
        addProgramHandler(res.data.doc);
        setMsg({
          error: false,
          loading: false,
          text: "Program added successfully!",
        });
      }
      setTimeout(onClose, 3000);
    } catch (err: any) {
      setMsg({
        error: true,
        loading: false,
        text: err.message,
      });
    }
  };

  const deleteProgram = async () => {
    setMsg({
      error: false,
      loading: true,
      text: "",
    });

    try {
      if (program) {
        const res = await apiInstance.delete(`/api/programs/${program.id}`);
        deleteProgramHandler(res.data.doc);
        setMsg({
          error: false,
          loading: false,
          text: "Program deleted successfully!",
        });
      }
      setTimeout(onClose, 3000);
    } catch (err: any) {
      setMsg({
        error: true,
        loading: false,
        text: err.message,
      });
    }
  };

  return (
    <div
      id="add-program-pp"
      className={`modal fade ${show ? "show d-block" : ""}`}
      tabIndex={-1}
      role="dialog"
      style={{ background: show ? "rgba(0,0,0,0.5)" : "transparent" }}
    >
      <div className="modal-dialog-centered w-50 m-auto" role="document">
        <div className="modal-content">
          {/* Header */}
          <div className="modal-header">
            <h5 className="modal-title text-secondary">
              {program ? "Edit" : "Add"} Program
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
            ></button>
          </div>

          {/* Body */}
          <div className="modal-body">
            <h5 className="fw-bold mb-5 text-center">
              {formData?.programName}
            </h5>

            <div className="card mb-4 shadow-sm border-0 rounded-3">
              <div className="card-body">
                <form>
                  <div className="row">
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="form-label">
                          Program Name <span className="text-danger"> *</span>
                        </label>
                        <input
                          type="text"
                          className={`form-control ${errorField === 'programName' ? 'is-invalid' : ''}`}
                          name="programName"
                          value={formData?.programName || ""}
                          onChange={handleInputChange}
                        />
                        {errorField === 'programName' && (
                          <div className="invalid-feedback d-block">{msg.text}</div>
                        )}
                      </div>
                    </div>

                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="form-label">
                          Application Fee
                        </label>
                        <input
                          type="text"
                          inputMode="decimal"
                          className={`form-control ${errorField === 'applicationFee' ? 'is-invalid' : ''}`}
                          name="applicationFee"
                          value={formData?.applicationFee || ""}
                          onChange={handleNumberChange}
                        />
                        {errorField === 'applicationFee' && (
                          <div className="invalid-feedback d-block">{msg.text}</div>
                        )}
                      </div>
                    </div>

                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="form-label">
                          Tuition Fee
                        </label>
                        <input
                          type="text"
                          inputMode="decimal"
                          className={`form-control ${errorField === 'tuitionFee' ? 'is-invalid' : ''}`}
                          name="tuitionFee"
                          value={formData?.tuitionFee || ""}
                          onChange={handleNumberChange}
                        />
                        {errorField === 'tuitionFee' && (
                          <div className="invalid-feedback d-block">{msg.text}</div>
                        )}
                      </div>
                    </div>

                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="form-label">
                          Program Level <span className="text-danger"> *</span>
                        </label>

                        <input
                          type="text"
                          className={`form-control ${errorField === 'programLevel' ? 'is-invalid' : ''}`}
                          name="programLevel"
                          placeholder="Enter Program Level"
                          value={formData?.programLevel || ""}
                          onChange={handleInputChange}
                        />
                        {errorField === 'programLevel' && (
                          <div className="invalid-feedback d-block">{msg.text}</div>
                        )}
                      </div>
                    </div>


                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="form-label">
                          Field of Study <span className="text-danger"> *</span>
                        </label>

                        <input
                          type="text"
                          className={`form-control ${errorField === 'field' ? 'is-invalid' : ''}`}
                          name="field"
                          placeholder="Enter Field of Study"
                          value={formData?.field || ""}
                          onChange={handleInputChange}
                        />
                        {errorField === 'field' && (
                          <div className="invalid-feedback d-block">{msg.text}</div>
                        )}
                      </div>
                    </div>


                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="form-label">
                          Start Date
                        </label>
                        <div className="input-icon-start position-relative">
                          <span className="input-icon-addon">
                            <i className="isax isax-calendar" />
                          </span>

                          <DatePicker
                            name="start"
                            className="form-control datetimepicker"
                            getPopupContainer={getModalContainer}
                            placeholder="Select Date"
                            format="DD/MM/YYYY"
                            value={formData?.start ? dayjs(formData.start) : null}
                            onChange={(date) => {
                              handleInputChange({
                                target: { name: "start", value: date ? date.format('YYYY-MM-DD') : '' },
                              });
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="form-label">
                          End Date
                        </label>
                        <div className="input-icon-start position-relative">
                          <span className="input-icon-addon">
                            <i className="isax isax-calendar" />
                          </span>

                          <DatePicker
                            name="end"
                            className="form-control datetimepicker"
                            status={errorField === 'end' ? 'error' : undefined}
                            getPopupContainer={getModalContainer}
                            placeholder="Select Date"
                            format="DD/MM/YYYY"
                            value={formData?.end ? dayjs(formData.end) : null}
                            onChange={(date) => {
                              handleInputChange({
                                target: { name: "end", value: date ? date.format('YYYY-MM-DD') : '' },
                              });
                            }}
                          />
                        </div>
                        {errorField === 'end' && (
                          <div className="invalid-feedback d-block">{msg.text}</div>
                        )}
                      </div>
                    </div>

                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="form-label">
                          Program Details
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          name="about"
                          value={formData?.about || ""}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="form-label">
                          Program Requirements
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          name="requirement"
                          value={formData?.requirement || ""}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                  </div>

                  {/* ✅ IMAGE UPLOAD FIELD */}
                  <div className="mb-3">
                    <label className="form-label">Program Image</label>
                    <input
                      key={`${show}-${program?.id || "new"}`}
                      type="file"
                      accept="image/*"
                      className="form-control"
                      onChange={handleImageUpload}
                    />
                    {uploading && (
                      <p className="text-info mt-2">Uploading image...</p>
                    )}
                    {preview && (
                      
                      <BackendImage 
                        src={preview}
                      />
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="modal-footer d-flex gap-3">
            {msg.loading && (
              <span className="mt-3 fs-14 fw-normal d-flex align-items-center justify-content-center">
                Saving program...
              </span>
            )}
            {!msg.loading && !errorField && (
              <span
                className={`mt-3 ${
                  msg.error ? "text-red" : ""
                } fs-14 fw-normal d-flex align-items-center justify-content-center`}
              >
                {msg.text}
              </span>
            )}
            <button
              type="button"
              className="btn btn-primary"
              onClick={saveProgram}
            >
              Save
            </button>
            {program && (
              <button
                type="button"
                className="btn btn-danger"
                onClick={deleteProgram}
              >
                Delete
              </button>
            )}
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddProgramPopup;
